/* hs-eslint ignored failing-rules */
/* eslint-disable react/prop-types */

'use es6';

import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import Immutable from 'immutable';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Small from 'UIComponents/elements/Small';
import UIButton from 'UIComponents/button/UIButton';
import UITruncateString from 'UIComponents/text/UITruncateString';
import FormattedList from 'I18n/components/FormattedList';
import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import { getHubSpotFeatureI18nKey } from 'ecosystem-marketplace-core/utils/i18n';
import { BY_CATEGORY as FEATURES_BY_CATEGORY } from 'ecosystem-marketplace-core/constants/groupTags/categoryFeatures';
import { BY_PRODUCT as HUBSPOT_FEATURES } from 'ecosystem-marketplace-core/constants/groupTags/hubspotFeatures';
import { FREE } from 'ecosystem-marketplace-core/constants/pricings/PlanType';
import MobilePanel from 'ecosystem-marketplace-core/js/components/mobile/MobilePanel';
import MobilePanelBody from 'ecosystem-marketplace-core/js/components/mobile/MobilePanelBody';
import MobilePanelFooter from 'ecosystem-marketplace-core/js/components/mobile/MobilePanelFooter';
import MobilePanelHeader from 'ecosystem-marketplace-core/js/components/mobile/MobilePanelHeader';
import MobilePanelNav from 'ecosystem-marketplace-core/js/components/mobile/MobilePanelNav';
import * as Sync from 'ecosystem-marketplace-core/constants/dataSyncs/Sync';
import { APP_CERTIFICATION_CREDENTIAL_ID } from 'marketplace-ui-apps-core/common/constants/Certification';
import { getCategoryFeatureI18nKey } from 'marketplace-ui-apps-core/common/utils/i18n';
import { CUSTOMER_SUPPORT_LANGUAGES as validLanguages } from 'marketplace-ui-common/constants/Languages';
import * as PlanType from 'marketplace-ui-common/constants/pricings/PlanType';
import * as SupportSource from 'marketplace-ui-common/constants/search/SupportSource';
import { useFocusTrap } from 'marketplace-ui-common/hooks/useFocusTrap';
import { getCategoryI18nKey, getTierI18nKey } from 'marketplace-ui-common/utils/i18n';
import * as GroupTagsActions from '../../../../actions/GroupTagActions';
import * as FilterKey from '../../../../constants/search/FilterKey';
import { PRICE } from '../../../../constants/search/SortField';
import { TierGroup, getTierGroupOrder } from '../../../../constants/tiers';
import StorefrontFilters from '../../../../records/StorefrontFilters';
import { getIsUngatedForContentHub } from '../../../../selectors/auth';
import { getAppIntegrations } from '../../../../selectors/appIntegrations';
import { getAppIntegrationsRequestState } from '../../../../selectors/requestStates';
import { getDataSyncFilterI18nKey, getPlanTypeFilterI18nKey, getSupportSourceFilterI18nKey } from '../../../../utils/i18n';
import MobileCheckboxGroup from './MobileCheckboxGroup';
import MobileFilterNavItemWrapper from './MobileFilterNavItemWrapper';
import MobileRadioGroup from './MobileRadioGroup';
import MobileSortPanel from './MobileSortPanel';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledHeaderAction = styled(UIButton).withConfig({
  displayName: "MobileFiltersPanel__StyledHeaderAction",
  componentId: "pdmxsk-0"
})(["&&&&{padding:4px;}"]);
const textPath = 'appMarketplace.storefront.filters';
const getHubSpotFeatureTopLevelText = key => unescapedText(`${textPath}.hubspotFeatures.items.${key}`);
const getCompatibilityTopLevelText = key => unescapedText(`${textPath}.compatibility.items.${key}`);
const UPDATE_ALL_FILTERS = 'UPDATE_ALL_FILTERS';
const UPDATE_FILTER = 'UPDATE_FILTER';
const ALL = null;
const pricings = [ALL, PlanType.FREE, PlanType.MONTHLY, PlanType.YEARLY, PlanType.ONE_TIME, PlanType.METERED, PlanType.CONTACT_US];
const dataSyncsOrder = Immutable.OrderedSet([ALL, Sync.INBOUND, Sync.OUTBOUND, Sync.BOTH]);
const supportOrder = Immutable.OrderedSet([ALL, SupportSource.HUBSPOT, SupportSource.APP_PARTNER]);
const getHubSpotFeaturesGroupsMap = isUngatedForContentHub => Immutable.OrderedMap({
  [getHubSpotFeatureTopLevelText('crm')]: HUBSPOT_FEATURES.CRM,
  [getHubSpotFeatureTopLevelText('marketing')]: HUBSPOT_FEATURES.MARKETING,
  [getHubSpotFeatureTopLevelText('sales')]: HUBSPOT_FEATURES.SALES,
  [getHubSpotFeatureTopLevelText('service')]: HUBSPOT_FEATURES.SERVICE,
  [getHubSpotFeatureTopLevelText(isUngatedForContentHub ? 'content' : 'cms')]: HUBSPOT_FEATURES.CMS,
  [getHubSpotFeatureTopLevelText('ops')]: HUBSPOT_FEATURES.OPS
}).map(groupFeatures => Immutable.Set(Object.values(groupFeatures)));
const getPlanCompatibilityGroupsMap = isUngatedForContentHub => {
  const tiersOrder = getTierGroupOrder(isUngatedForContentHub);
  let tempTiersObject = {};
  tiersOrder.forEach(tier => {
    if (tier instanceof TierGroup) {
      tempTiersObject = Object.assign({}, tempTiersObject, {
        [getCompatibilityTopLevelText(tier.get('key'))]: Immutable.Set(tier.get('tiers'))
      });
    } else {
      tempTiersObject = Object.assign({}, tempTiersObject, {
        [tier]: tier
      });
    }
  });
  return Immutable.OrderedMap(tempTiersObject);
};
const FilterSectionText = ({
  title,
  subtitle
}) => {
  return /*#__PURE__*/_jsxs("div", {
    children: [title, /*#__PURE__*/_jsx("br", {}), /*#__PURE__*/_jsx(Small, {
      use: "help",
      children: subtitle
    })]
  });
};
const CommaSeparatedList = ({
  options,
  title
}) => {
  const optionsArray = options.toArray();
  const displayedTitle = title ? `${title}${options.size ? ': ' : ''}` : '';
  return /*#__PURE__*/_jsxs(UITruncateString, {
    children: [displayedTitle, /*#__PURE__*/_jsx(FormattedList, {
      children: optionsArray.map(option => option)
    })]
  });
};
function reducer(activeFilters, action) {
  switch (action.type) {
    case UPDATE_ALL_FILTERS:
      return action.payload;
    case UPDATE_FILTER:
      return activeFilters.set(action.payload.key, action.payload.value);
    default:
      return activeFilters;
  }
}
const getSubtitleOptions = (options, translateOption, defaultValue = '') => {
  if (!options) {
    return Immutable.Set([defaultValue]);
  }
  if (Immutable.Set.isSet(options)) {
    return options.map(option => translateOption(option));
  }
  return Immutable.Set([translateOption(options)]);
};
const MobileFiltersPanel = ({
  appIntegrations,
  filters,
  onUpdateFilters,
  onPanelClose,
  open,
  searchTerm
}) => {
  const isUngatedForContentHub = useSelector(getIsUngatedForContentHub);
  const [activeFilters, dispatch] = useReducer(reducer, filters);
  const [activePanelIds, setActivePanelIds] = useState([]);
  const filterPanelRef = useRef(null);
  useEffect(() => {
    if (open) {
      filterPanelRef.current.focus();
    }
  }, [open]);
  const panelDataBlob = useMemo(() => {
    return {
      [FilterKey.CATEGORY_FEATURES]: filters.get(FilterKey.CATEGORY) ? {
        panelTitle: `${textPath}.categoryFeatures.titleText`,
        options: Object.values(FEATURES_BY_CATEGORY[filters.get(FilterKey.CATEGORY)]),
        multi: true,
        hidden: !filters.get(FilterKey.CATEGORY),
        searchable: true,
        searchPlaceholder: `${textPath}.categoryFeatures.searchInputPlaceholder`,
        searchPlaceholderOptions: {
          category: unescapedText(getCategoryI18nKey(filters.get(FilterKey.CATEGORY)))
        },
        categoryText: unescapedText(getCategoryI18nKey(filters.get(FilterKey.CATEGORY))),
        translateOption: option => unescapedText(getCategoryFeatureI18nKey(filters.get(FilterKey.CATEGORY), option))
      } : {
        hidden: true
      },
      [FilterKey.PRICING]: {
        panelTitle: `${textPath}.planType.titleText`,
        options: pricings,
        defaultValue: unescapedText(`${textPath}.planType.items.all`),
        translateOption: option => unescapedText(getPlanTypeFilterI18nKey(option))
      },
      [FilterKey.TIERS]: {
        panelTitle: `${textPath}.compatibility.titleText`,
        options: getPlanCompatibilityGroupsMap(isUngatedForContentHub),
        multi: true,
        nested: true,
        fullySelectable: true,
        translateOption: option => unescapedText(getTierI18nKey(option))
      },
      [FilterKey.HUBSPOT_FEATURES]: {
        panelTitle: `${textPath}.hubspotFeatures.titleText`,
        options: getHubSpotFeaturesGroupsMap(isUngatedForContentHub),
        multi: true,
        searchable: true,
        searchPlaceholder: `${textPath}.hubspotFeatures.searchInputPlaceholder`,
        translateOption: option => unescapedText(getHubSpotFeatureI18nKey(option))
      },
      [FilterKey.SYNC]: {
        panelTitle: `${textPath}.sync.titleText`,
        options: dataSyncsOrder,
        defaultValue: unescapedText(`${textPath}.sync.items.all`),
        translateOption: option => unescapedText(getDataSyncFilterI18nKey(option))
      },
      [FilterKey.CREDENTIALS]: {
        panelTitle: `${textPath}.certified.titleText`,
        options: [APP_CERTIFICATION_CREDENTIAL_ID],
        multi: true,
        translateOption: () => unescapedText(`${textPath}.certified.item`)
      },
      [FilterKey.INTEGRATIONS]: {
        panelTitle: `${textPath}.integrations.titleText`,
        options: appIntegrations,
        multi: true,
        limitResults: true,
        searchable: true,
        searchPlaceholder: `${textPath}.integrations.searchInputPlaceholder`,
        translateOption: option => option
      },
      [FilterKey.SUPPORT_SOURCE]: {
        panelTitle: `${textPath}.supportSource.titleText`,
        options: supportOrder,
        defaultValue: unescapedText(`${textPath}.supportSource.items.all`),
        translateOption: option => unescapedText(getSupportSourceFilterI18nKey(option))
      },
      [FilterKey.SUPPORT_LANGUAGES]: {
        panelTitle: `${textPath}.supportedLanguages.titleText`,
        options: validLanguages,
        multi: true,
        searchable: true,
        searchPlaceholder: `${textPath}.supportedLanguages.searchInputPlaceholder`,
        translateOption: option => unescapedText(`SharedI18nStrings.languageNames.${option}`)
      }
    };
  }, [appIntegrations, filters, isUngatedForContentHub]);
  const clickFilter = useCallback(value => {
    if (activePanelIds && activePanelIds.indexOf(value) === -1) {
      setActivePanelIds(panelIds => [...panelIds, value]);
    }
  }, [activePanelIds, setActivePanelIds]);
  const closePanel = useCallback(value => {
    filterPanelRef.current.focus();
    setActivePanelIds(activePanelIds.filter(panelId => panelId !== value));
  }, [activePanelIds]);
  const clearFilters = useCallback(() => {
    const freshState = StorefrontFilters.from({
      superCategory: filters.get(FilterKey.SUPER_CATEGORY),
      category: filters.get(FilterKey.CATEGORY),
      search: filters.get(FilterKey.SEARCH)
    });
    onUpdateFilters(freshState);
    setActivePanelIds([]);
    onPanelClose();
    dispatch({
      type: UPDATE_ALL_FILTERS,
      payload: freshState
    });
  }, [filters, onPanelClose, onUpdateFilters]);
  const applyFilters = useCallback(() => {
    onUpdateFilters(activeFilters);
    setActivePanelIds([]);
    onPanelClose();
  }, [onUpdateFilters, onPanelClose, setActivePanelIds, activeFilters]);
  const handleClose = useCallback(() => {
    onPanelClose();
    dispatch({
      type: UPDATE_ALL_FILTERS,
      payload: filters
    });
  }, [dispatch, filters, onPanelClose]);
  const handleSortChange = useCallback((sort, order) => {
    dispatch({
      type: UPDATE_FILTER,
      payload: {
        key: FilterKey.SORT_FIELD,
        value: sort
      }
    });
    dispatch({
      type: UPDATE_FILTER,
      payload: {
        key: FilterKey.SORT_ORDER,
        value: order
      }
    });
  }, [dispatch]);
  const pricingSortEnabled = useMemo(() => {
    if (activeFilters.get(FilterKey.PRICING) === ALL || activeFilters.get(FilterKey.PRICING) === FREE) {
      if (activeFilters.get(FilterKey.SORT_FIELD) === PRICE) {
        dispatch({
          type: UPDATE_FILTER,
          payload: {
            key: FilterKey.SORT_FIELD,
            value: null
          }
        });
        dispatch({
          type: UPDATE_FILTER,
          payload: {
            key: FilterKey.SORT_ORDER,
            value: null
          }
        });
      }
      return false;
    }
    return true;
  }, [activeFilters, dispatch]);
  const renderedPanels = useMemo(() => {
    const panels = [];
    Immutable.OrderedMap(panelDataBlob).forEach((filterData, filterKey) => {
      if (!filterData.hidden) {
        panels.push( /*#__PURE__*/_jsx(MobileFilterNavItemWrapper, {
          applyFilters: applyFilters,
          clickFilter: clickFilter,
          filterKey: filterKey,
          onPanelClose: () => closePanel(filterKey),
          open: activePanelIds && activePanelIds.indexOf(filterKey) !== -1,
          panelTitle: /*#__PURE__*/_jsx(FormattedMessage, {
            message: filterData.panelTitle
          }),
          searchEnabled: filterData.searchable,
          textSection: /*#__PURE__*/_jsx(FilterSectionText, {
            title: /*#__PURE__*/_jsx(FormattedMessage, {
              message: filterData.panelTitle
            }),
            subtitle: /*#__PURE__*/_jsx(CommaSeparatedList, {
              options: getSubtitleOptions(activeFilters.get(filterKey), filterData.translateOption),
              defaultValue: filterData.defaultValue,
              title: filterData.categoryText
            })
          }),
          children: filterData.multi ? /*#__PURE__*/_jsx(MobileCheckboxGroup, {
            defaultDisplayCount: filterData.limitResults && 10,
            fullySelectable: filterData.fullySelectable,
            name: filterKey,
            options: filterData.options,
            translateOption: filterData.translateOption,
            value: activeFilters.get(filterKey),
            searchEnabled: filterData.searchable,
            searchPlaceholder: filterData.searchable && unescapedText(filterData.searchPlaceholder, filterData.searchPlaceholderOptions),
            onChange: value => dispatch({
              type: UPDATE_FILTER,
              payload: {
                key: filterKey,
                value
              }
            })
          }) : /*#__PURE__*/_jsx(MobileRadioGroup, {
            defaultValue: filterData.defaultValue,
            options: filterData.options,
            translateOption: filterData.translateOption,
            value: activeFilters.get(filterKey),
            searchEnabled: filterData.searchable,
            searchPlaceholder: filterData.searchable && unescapedText(filterData.searchPlaceholder, filterData.searchPlaceholderOptions),
            onChange: value => dispatch({
              type: UPDATE_FILTER,
              payload: {
                key: filterKey,
                value
              }
            })
          })
        }, filterKey));
      }
    });
    return panels;
  }, [activeFilters, activePanelIds, applyFilters, clickFilter, closePanel, panelDataBlob]);
  useFocusTrap(open);
  return open && /*#__PURE__*/_jsxs(MobilePanel, {
    align: 'bottom',
    open: open,
    children: [/*#__PURE__*/_jsx(MobilePanelHeader, {
      itemLeft: /*#__PURE__*/_jsx(StyledHeaderAction, {
        buttonRef: filterPanelRef,
        className: "m-left-2",
        use: "transparent",
        onClick: handleClose,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `${textPath}.cancel`
        })
      }),
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `${textPath}.filter`
      }),
      itemRight: /*#__PURE__*/_jsx(StyledHeaderAction, {
        className: "m-right-3",
        use: "transparent",
        onClick: applyFilters,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `${textPath}.apply`
        })
      })
    }), /*#__PURE__*/_jsxs(MobilePanelBody, {
      children: [/*#__PURE__*/_jsx(MobilePanelNav, {
        title: unescapedText('appMarketplace.storefront.results.sort.mobileLabel'),
        children: /*#__PURE__*/_jsx(MobileSortPanel, {
          activeSort: activeFilters.get(FilterKey.SORT_FIELD),
          activeOrder: activeFilters.get(FilterKey.SORT_ORDER),
          applyFilters: applyFilters,
          activePanelIds: activePanelIds,
          clickFilter: clickFilter,
          closePanel: closePanel,
          onSortChange: handleSortChange,
          pricingSortEnabled: pricingSortEnabled,
          searchTerm: searchTerm
        })
      }), /*#__PURE__*/_jsx(MobilePanelNav, {
        title: unescapedText(`${textPath}.mobileLabel`),
        children: renderedPanels
      })]
    }), /*#__PURE__*/_jsx(MobilePanelFooter, {
      children: /*#__PURE__*/_jsx(UIButton, {
        use: "link",
        onClick: () => {
          clearFilters();
        },
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `${textPath}.clearAll`
        })
      })
    })]
  });
};
MobileFiltersPanel.propTypes = {
  filters: PropTypes.instanceOf(StorefrontFilters).isRequired,
  onUpdateFilters: PropTypes.func.isRequired,
  onPanelClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  searchTerm: PropTypes.string.isRequired
};
const mapStateToProps = activeFilters => {
  return {
    appIntegrations: getAppIntegrations(activeFilters),
    appIntegrationsRequestState: getAppIntegrationsRequestState(activeFilters)
  };
};
const mapDispatchToProps = {
  fetchAppIntegrations: GroupTagsActions.fetchAppIntegrations
};
export default connect(mapStateToProps, mapDispatchToProps)(MobileFiltersPanel);